import type { JobDetailsStore } from "../../../stores/job-details.store";
import type {
  ActionOutputData,
  OutputData,
  ValueData,
} from "@smartsuite/automation-engine-api";
import { valueDataToStringsWithDelimenters } from "@smartsuite/automation-conditions";

/**
 * Builds URL to open a record in edit mode: solution base path + ?editRecord=recordId.
 * Uses solutionId/applicationId from params or extracts from window.location when not set.
 * Path is built as /solution/{solutionId}/{applicationId}/ only (omits viewId, /automations,
 * /history and everything after). Falls back to stripping from /automations when no solution path.
 */
export const getEditRecordUrl = (
  recordId: string,
  solutionId?: string,
  applicationId?: string,
): string => {
  const url = new URL(window.location.href);
  const pathSegments = url.pathname.split("/").filter(Boolean);
  const solutionIndex = pathSegments.indexOf("solution");

  if (solutionIndex <= 0) {
    // 0 or -1
    // dev mode
    return `${url.origin}${
      url.pathname.split("/automations")[0]
    }?editRecord=${encodeURIComponent(recordId)}`;
  }

  const resolvedSolutionId = solutionId ?? pathSegments[solutionIndex + 1];
  const resolvedApplicationId =
    applicationId ?? pathSegments[solutionIndex + 2];

  const accountPrefix = pathSegments[0];
  const basePath = `/${accountPrefix}/solution/${resolvedSolutionId}/${resolvedApplicationId}/`;

  return `${url.origin}${basePath}?editRecord=${encodeURIComponent(recordId)}`;
};

export const isOldTypejob = (jobDetails: JobDetailsStore): boolean => {
  return (
    jobDetails.job.actionGroupsStatus === undefined ||
    (Array.isArray(jobDetails.job.actionGroupsStatus) &&
      jobDetails.job.actionGroupsStatus.length === 0) ||
    (Object.prototype.toString.call(jobDetails.job.actionGroupsStatus) ===
      "[object Object]" &&
      Object.keys(jobDetails.job.actionGroupsStatus).length === 0)
  );
};

export const getRecordListOutputs = (
  outputData: ActionOutputData,
): OutputData[] => {
  if (outputData.recordList?.recordList === undefined) {
    return [];
  }
  return outputData.recordList.recordList.records[0]?.outputs;
};

export const findOutputValueAndLabel = (
  outputId: string,
  outputs: OutputData[],
): string | undefined => {
  const output = outputs.find((output) => output.outputId === outputId);
  if (output === undefined) {
    return undefined;
  }

  return getOutputValue(output.value);
};

export const getOutputValue = (value: ValueData | undefined): string => {
  return valueDataToStringsWithDelimenters(value).join("; ");
};

export const filterEntireFieldsFromRecordJobData = (
  outputs: OutputData[],
): OutputData[] => {
  return outputs.filter((output) => {
    //   filter the same field
    if (
      outputs.some((output2) => "field-" + output.outputId === output2.outputId)
    ) {
      return false;
    }
    // filter entire field
    if (!isFieldWithSuffix(output)) {
      const isExistWithSuffix = outputs.some((output2) => {
        return (
          output2.outputId.includes(output.outputId) &&
          isFieldWithSuffix(output2)
        );
      });
      return !isExistWithSuffix;
    }
    return true;
  });
};

const isFieldWithSuffix = (input: OutputData): boolean => {
  return input.outputId.includes(".");
};
